import { Plug, Radio } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Badge, Card, CardContent, CardHeader } from '@/components/ui'

import { riskLevelMeta } from '../riskLevel'
import type { IntegrationConnector, IntegrationTool } from '../types'

export interface IntegrationConnectorCardProps {
  readonly connector: IntegrationConnector
}

/** Badge de nivel de risco de uma consulta: traduz o valor cru; desconhecido cai em neutro + texto cru. */
function RiskLevelBadge({ riskLevel }: { readonly riskLevel: string }) {
  const { t } = useTranslation()
  const meta = riskLevelMeta(riskLevel)

  if (meta === null) {
    return <Badge variant="neutral">{riskLevel}</Badge>
  }

  return <Badge variant={meta.badgeVariant}>{t(meta.labelKey)}</Badge>
}

/**
 * Uma consulta ao vivo: o destaque e a `description` (o que a consulta retorna, em linguagem de
 * produto); o `name` tecnico aparece discreto em monospace, para quem quiser referencia-lo.
 */
function IntegrationToolRow({ tool }: { readonly tool: IntegrationTool }) {
  return (
    <li className="flex flex-wrap items-start justify-between gap-3 rounded-md border border-border-default bg-background-subtle px-4 py-3">
      <div className="flex min-w-0 flex-col gap-1">
        <p className="text-sm text-foreground-primary">{tool.description}</p>
        <code className="font-mono text-xs text-foreground-muted">{tool.name}</code>
      </div>
      <RiskLevelBadge riskLevel={tool.riskLevel} />
    </li>
  )
}

/**
 * Card de um conector: icone + nome, o badge de saude sempre visivel (Conectado=verde,
 * Indisponivel=warning) e a lista das consultas ao vivo (tools) que o agente pode fazer ao responder
 * um cliente. Vocabulario neutro (docs/design/00 s5): nunca cita SQL/HTTP/SDK/read model/RAG.
 */
export function IntegrationConnectorCard({ connector }: IntegrationConnectorCardProps) {
  const { t } = useTranslation()
  const healthLabel = connector.healthy
    ? t('integrations.health.healthy')
    : t('integrations.health.unavailable')

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <span
              aria-hidden="true"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-background-subtle text-foreground-secondary"
            >
              <Plug className="h-5 w-5" />
            </span>
            <div className="flex flex-col gap-0.5">
              <h3 className="text-base font-semibold text-foreground-primary">
                {connector.displayName}
              </h3>
              <span className="text-xs text-foreground-muted">{connector.key}</span>
            </div>
          </div>

          <Badge variant={connector.healthy ? 'success' : 'warning'}>{healthLabel}</Badge>
        </div>
      </CardHeader>

      <CardContent className="flex flex-col gap-3">
        <p className="text-sm text-foreground-secondary">{t('integrations.card.toolsHint')}</p>

        {connector.tools.length === 0 ? (
          <div className="flex items-center gap-2 rounded-md border border-dashed border-border-default bg-background-subtle px-4 py-3 text-sm text-foreground-muted">
            <Radio aria-hidden="true" className="h-4 w-4 shrink-0" />
            <span>{t('integrations.card.noTools')}</span>
          </div>
        ) : (
          <ul className="flex flex-col gap-2" aria-label={t('integrations.card.toolsLabel')}>
            {connector.tools.map((tool) => (
              <IntegrationToolRow key={tool.name} tool={tool} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}
